import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center min-w-20 px-4 rounded-[2px] font-pp-neue-montreal text-[12px] leading-none tracking-[0.5px] uppercase whitespace-nowrap transition-colors select-none outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:pointer-events-none aria-busy:pointer-events-none',
  {
    variants: {
      variant: {
        'primary-solid': 'font-semibold',
        'secondary-outline': 'font-semibold border border-solid bg-transparent',
        'tertiary-link': 'font-[350]',
      },
      palette: {
        dark: '',
        white: '',
      },
      size: {
        lg: 'h-13',
        md: 'h-[46px]',
        sm: 'h-9',
      },
    },
    compoundVariants: [
      // Primary/Solid — Dark
      {
        variant: 'primary-solid',
        palette: 'dark',
        className:
          'bg-jncquoi-neutral-1000 text-jncquoi-neutral-00 active:bg-jncquoi-neutral-800 aria-busy:bg-jncquoi-neutral-900 disabled:bg-jncquoi-neutral-600',
      },
      // Primary/Solid — White
      {
        variant: 'primary-solid',
        palette: 'white',
        className:
          'bg-jncquoi-neutral-00 text-jncquoi-neutral-1000 active:text-jncquoi-neutral-900 aria-busy:text-jncquoi-neutral-900 disabled:bg-jncquoi-neutral-50 disabled:text-jncquoi-neutral-600',
      },
      // Secondary/Outline — Dark
      {
        variant: 'secondary-outline',
        palette: 'dark',
        className:
          'border-jncquoi-neutral-1000 text-jncquoi-neutral-1000 aria-busy:border-jncquoi-neutral-800 aria-busy:text-jncquoi-neutral-800 disabled:border-jncquoi-neutral-800 disabled:text-jncquoi-neutral-600',
      },
      // Secondary/Outline — White
      {
        variant: 'secondary-outline',
        palette: 'white',
        className:
          'border-jncquoi-neutral-00 text-jncquoi-neutral-00 active:text-jncquoi-neutral-50 aria-busy:text-jncquoi-neutral-50 disabled:border-jncquoi-neutral-100 disabled:text-jncquoi-neutral-400',
      },
      // Tertiary/Link — Dark
      {
        variant: 'tertiary-link',
        palette: 'dark',
        className: 'text-jncquoi-neutral-1000 disabled:text-jncquoi-neutral-700',
      },
      // Tertiary/Link — White
      {
        variant: 'tertiary-link',
        palette: 'white',
        className:
          'text-jncquoi-neutral-00 active:text-jncquoi-neutral-50 aria-busy:text-jncquoi-neutral-50 disabled:text-jncquoi-neutral-400',
      },
    ],
    defaultVariants: {
      variant: 'primary-solid',
      palette: 'dark',
      size: 'lg',
    },
  }
);

function Button({
  className,
  variant = 'primary-solid',
  palette = 'dark',
  size = 'lg',
  loading = false,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, palette, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
